import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Avatar, Chip,
  TextField, MenuItem, Button, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  InputAdornment, Tooltip, IconButton, Switch, FormControlLabel,
  ToggleButtonGroup, ToggleButton, Collapse, Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Edit as AdjustIcon, Search as SearchIcon, Refresh as RefreshIcon,
  Person as PersonIcon, RemoveCircleOutline as WriteoffIcon,
  AddCircleOutline as AddBillIcon, Save as SaveIcon, Check as ApproveIcon,
  Close as RejectIcon, History as HistoryIcon, ReportProblem as PendingIcon,
  ExpandMore as ExpandIcon, ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import { useAuth } from '../../context/AuthContext';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleString();
};

function statusChip(status) {
  switch (status) {
    case 'POSTED':   return { color: 'success', label: 'POSTED' };
    case 'PENDING':  return { color: 'warning', label: 'PENDING' };
    case 'REJECTED': return { color: 'error',   label: 'REJECTED' };
    case 'REVERSED': return { color: 'default', label: 'REVERSED' };
    default:         return { color: 'default', label: status || '—' };
  }
}

// ── Pending approvals strip ────────────────────────────────────
function PendingApprovalsStrip({ items, onRefresh, onApprove, onReject, canApprove }) {
  const [open, setOpen] = useState(true);
  if (items.length === 0) return null;
  return (
    <Card sx={{ mb: 2, borderLeft: (t) => `4px solid ${t.palette.warning.main}` }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PendingIcon color="warning" />
          <Typography fontWeight={800}>Pending Approvals</Typography>
          <Chip size="small" color="warning" label={items.length} sx={{ fontWeight: 800 }} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh"><IconButton size="small" onClick={onRefresh}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
          <IconButton size="small" onClick={() => setOpen(o => !o)}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </Stack>
        <Collapse in={open}>
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 260 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, py: 0.6 } }}>
                  <TableCell>When</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Created by</TableCell>
                  <TableCell align="right" sx={{ width: 130 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(it => (
                  <TableRow key={it.transaction_id} hover sx={{ '& td': { fontSize: 12, py: 0.7, ...TNUM } }}>
                    <TableCell>{fmtDateTime(it.created_at)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{it.student_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{it.student_id}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...TNUM, fontWeight: 600 }}>{it.acc_name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={it.type === 'writeoff' ? 'Write-off' : 'Add Bill'}
                        color={it.type === 'writeoff' ? 'error' : 'info'}
                        variant="outlined" sx={{ fontWeight: 700, height: 20 }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>GHS {fmtMoney(it.amount)}</TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={it.reason || ''}><span>{it.reason}</span></Tooltip>
                    </TableCell>
                    <TableCell>{it.created_by_name || '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={canApprove ? 'Approve' : 'Needs the Approve Balance Adjustments right (Settings → Roles)'}>
                        <span>
                          <IconButton size="small" color="success" disabled={!canApprove}
                            onClick={() => onApprove(it)}><ApproveIcon fontSize="small" /></IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={canApprove ? 'Reject' : 'Needs the Approve Balance Adjustments right (Settings → Roles)'}>
                        <span>
                          <IconButton size="small" color="error" disabled={!canApprove}
                            onClick={() => onReject(it)}><RejectIcon fontSize="small" /></IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </CardContent>
    </Card>
  );
}

// ── Adjustment history table for one student ───────────────────
function AdjustmentHistory({ items, loading }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <HistoryIcon color="primary" />
          <Typography fontWeight={800}>Adjustment History</Typography>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" label={`${items.length}`} sx={{ fontWeight: 700 }} />
        </Stack>
        {loading ? (
          <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No adjustments on record for this student.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, py: 0.6 } }}>
                  <TableCell>When</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Old → New</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>By / Approver</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(it => {
                  const sc = statusChip(it.status);
                  return (
                    <TableRow key={it.transaction_id} hover sx={{ '& td': { fontSize: 12, py: 0.7, ...TNUM } }}>
                      <TableCell>{fmtDateTime(it.created_at)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{it.acc_name}</TableCell>
                      <TableCell>
                        <Chip size="small" label={it.type === 'writeoff' ? 'Write-off' : 'Add Bill'}
                          color={it.type === 'writeoff' ? 'error' : 'info'}
                          variant="outlined" sx={{ fontWeight: 700, height: 20 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>GHS {fmtMoney(it.amount)}</TableCell>
                      <TableCell align="right" sx={TNUM}>
                        {fmtMoney(it.old_balance)} → <b>{fmtMoney(it.new_balance)}</b>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={it.reason || ''}><span>{it.reason}</span></Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={sc.color} label={sc.label}
                          sx={{ fontWeight: 800, height: 20 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ display: 'block' }}>{it.created_by_name || '—'}</Typography>
                        {it.approved_by_name && (
                          <Typography variant="caption" color="text.secondary">→ {it.approved_by_name}</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

// ── Top-level Adjust Balances tab ──────────────────────────────
export default function AdjustBalancesTab() {
  const { user } = useAuth();
  // Approval right is granted via Roles → Approvals (or directly to a user).
  // super_admin always passes — that's enforced server-side too.
  const canApprove = user?.role === 'super_admin'
    || (user?.approvals || []).includes('adjustment.balance');

  // Picker
  const [students, setStudents]  = useState([]);
  const [studentsLoading, setSL] = useState(false);
  const [search, setSearch]      = useState('');
  const [filterCla, setFCla]     = useState('');
  const [classes, setClasses]    = useState([]);

  // Selection
  const [picked, setPicked] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accLoading, setAL] = useState(false);

  // Form
  const [form, setForm] = useState({
    acc_name: '', type: 'writeoff', amount: '', reason: '',
    reference_doc: '', requires_approval: true,
  });
  const [submitting, setSubmit] = useState(false);

  // History + pending
  const [history, setHistory] = useState([]);
  const [historyLoading, setHL] = useState(false);
  const [pending, setPending] = useState([]);

  // ── Loaders ───────────────────────────────────────────────────
  useEffect(() => {
    API.get('/classes').then(r => setClasses((r.data || []).map(c => c.class).filter(Boolean))).catch(() => {});
    loadPending();
    /* eslint-disable-next-line */
  }, []);

  const loadStudents = async () => {
    setSL(true);
    try {
      const r = await API.get('/payments/students', {
        params: { limit: 500, ...(filterCla ? { cla: filterCla } : {}) },
      });
      setStudents(r.data || []);
    } catch { /* silent */ }
    finally { setSL(false); }
  };
  useEffect(() => { loadStudents(); /* eslint-disable-next-line */ }, [filterCla]);

  const loadPending = async () => {
    try {
      const r = await API.get('/adjustments/pending');
      setPending(r.data || []);
    } catch { setPending([]); }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return students;
    return students.filter(s =>
      (s.firstname || '').toLowerCase().includes(term) ||
      (s.lastname  || '').toLowerCase().includes(term) ||
      (s.index_no  || '').toLowerCase().includes(term)
    );
  }, [students, search]);

  const reloadStudent = async (idx) => {
    if (!idx) return;
    setAL(true); setHL(true);
    try {
      const [accRes, hRes] = await Promise.all([
        API.get(`/payments/students/${idx}/fee-accounts`),
        API.get(`/adjustments/students/${idx}`),
      ]);
      setAccounts(accRes.data?.accounts || []);
      setHistory(hRes.data || []);
    } catch { setAccounts([]); setHistory([]); }
    finally { setAL(false); setHL(false); }
  };

  useEffect(() => {
    if (!picked) {
      setAccounts([]); setHistory([]);
      setForm(p => ({ ...p, acc_name: '', amount: '', reason: '', reference_doc: '' }));
      return;
    }
    reloadStudent(picked.index_no);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked]);

  // ── Submit ────────────────────────────────────────────────────
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const selectedAccount = useMemo(
    () => accounts.find(a => a.account === form.acc_name) || null,
    [accounts, form.acc_name]
  );

  const amountNum = Number(form.amount);
  const overWriteoff = form.type === 'writeoff'
    && selectedAccount
    && Number.isFinite(amountNum)
    && amountNum > Number(selectedAccount.balance) + 0.01;

  const newBalancePreview = useMemo(() => {
    if (!selectedAccount || !Number.isFinite(amountNum) || amountNum <= 0) return null;
    const bal = Number(selectedAccount.balance);
    return form.type === 'writeoff' ? bal - amountNum : bal + amountNum;
  }, [selectedAccount, amountNum, form.type]);

  const canSubmit = picked && form.acc_name && form.type
    && Number(form.amount) > 0
    && form.reason.trim().length >= 5
    && !overWriteoff
    && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    const willPost = !form.requires_approval && canApprove;
    const ok = await swal.confirm({
      title: willPost ? 'Apply adjustment now?' : 'Submit for approval?',
      tone: form.type === 'writeoff' ? 'warning' : 'primary',
      icon: willPost ? 'warning' : 'question',
      html: `<div style="text-align:left;font-size:0.92rem">
        <div><b>${picked.firstname} ${picked.lastname}</b> · ${picked.index_no}</div>
        <div style="margin-top:6px;color:#475569">
          ${form.type === 'writeoff' ? 'Write-off (Debit)' : 'Add Bill (Credit)'} · ${form.acc_name}
        </div>
        <div style="margin-top:8px">
          Old balance: GHS ${fmtMoney(selectedAccount?.balance)}
          → New balance: <b>GHS ${fmtMoney(newBalancePreview)}</b>
        </div>
        <div style="margin-top:6px;color:#475569"><i>${form.reason}</i></div>
      </div>`,
      confirmText: willPost ? 'Yes, apply now' : 'Yes, submit',
    });
    if (!ok) return;

    setSubmit(true);
    try {
      const r = await API.post('/adjustments', {
        student_id:    picked.index_no,
        acc_name:      form.acc_name,
        type:          form.type,
        amount:        Number(form.amount),
        reason:        form.reason.trim(),
        reference_doc: form.reference_doc.trim() || null,
        term:          selectedAccount?.term || '',
        acc_yer:       selectedAccount?.acc_yer || '',
        requires_approval: form.requires_approval,
      });
      if (r.data.status === 'POSTED') {
        swal.success(
          `Balance updated · GHS ${fmtMoney(r.data.old_balance)} → ${fmtMoney(r.data.new_balance)}`,
          'Adjustment posted'
        );
      } else {
        swal.success('Submitted for approval — admin will review.', 'Pending');
      }
      setForm(p => ({ ...p, amount: '', reason: '', reference_doc: '' }));
      reloadStudent(picked.index_no);
      loadPending();
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not save adjustment'));
    } finally { setSubmit(false); }
  };

  // ── Approve / Reject from the Pending strip ───────────────────
  const handleApprove = async (it) => {
    const ok = await swal.confirm({
      title: 'Approve adjustment?',
      tone: 'success', icon: 'question',
      html: `<div style="text-align:left;font-size:0.92rem">
        <div><b>${it.student_name}</b> · ${it.student_id}</div>
        <div style="margin-top:6px;color:#475569">
          ${it.type === 'writeoff' ? 'Write-off' : 'Add Bill'} · ${it.acc_name} · GHS ${fmtMoney(it.amount)}
        </div>
        <div style="margin-top:6px;color:#475569"><i>${it.reason}</i></div>
      </div>`,
      confirmText: 'Yes, approve',
    });
    if (!ok) return;
    try {
      const r = await API.post(`/adjustments/${it.transaction_id}/approve`);
      swal.success(
        `GHS ${fmtMoney(r.data.old_balance)} → ${fmtMoney(r.data.new_balance)}`,
        'Approved & posted'
      );
      loadPending();
      if (picked?.index_no === it.student_id) reloadStudent(picked.index_no);
    } catch (e) { swal.error(swal.pickError(e, 'Approve failed')); }
  };

  const handleReject = async (it) => {
    const reason = await swal.prompt({
      title: 'Reject adjustment',
      text: 'Why is this being rejected?',
      inputLabel: 'Reason',
      inputType: 'textarea',
      confirmText: 'Reject',
      validate: (v) => (!v || v.trim().length < 3) ? 'Reason is required' : null,
    });
    if (!reason) return;
    try {
      await API.post(`/adjustments/${it.transaction_id}/reject`, { reason });
      swal.success('Adjustment rejected', 'Done');
      loadPending();
    } catch (e) { swal.error(swal.pickError(e, 'Reject failed')); }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <Box sx={{ fontFamily: FONT_ACCT }}>
      <PendingApprovalsStrip
        items={pending} onRefresh={loadPending}
        onApprove={handleApprove} onReject={handleReject}
        canApprove={canApprove}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '0.85fr 1.7fr 1fr' }, gap: 2 }}>
        {/* ── Picker ──────────────────────────────────── */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <PersonIcon color="primary" />
              <Typography fontWeight={800}>Select Student</Typography>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Refresh"><span><IconButton size="small" onClick={loadStudents} disabled={studentsLoading}><RefreshIcon fontSize="small" /></IconButton></span></Tooltip>
            </Stack>
            <Stack spacing={1} sx={{ mb: 1 }}>
              <TextField fullWidth size="small" placeholder="Search by name or index"
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
              <TextField select size="small" label="Class" value={filterCla}
                onChange={(e) => setFCla(e.target.value)}>
                <MenuItem value=""><em>All classes</em></MenuItem>
                {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Stack>
            <Box sx={{ maxHeight: 460, overflowY: 'auto', pr: 0.5 }}>
              {studentsLoading && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={20} /></Box>}
              {!studentsLoading && filtered.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  No matches.
                </Typography>
              )}
              {!studentsLoading && filtered.map(s => {
                const sel = picked?.index_no === s.index_no;
                return (
                  <Paper key={s.trans_code} variant="outlined" onClick={() => setPicked(s)}
                    sx={{
                      px: 1.2, py: 0.9, mb: 0.6, cursor: 'pointer',
                      borderColor: sel ? 'primary.main' : 'divider',
                      borderWidth: sel ? 2 : 1,
                      bgcolor: sel ? (t) => alpha(t.palette.primary.main, 0.08) : 'background.paper',
                      '&:hover': { borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
                    }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'primary.main' }}>
                        {(s.firstname?.[0] || '?').toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={sel ? 800 : 700} noWrap>
                          {s.firstname} {s.lastname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={TNUM} noWrap>
                          {s.index_no} · {s.calss || '—'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Box>
          </CardContent>
        </Card>

        {/* ── Fee Accounts (click to pick for adjustment) ─────── */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <AdjustIcon color="primary" />
              <Typography fontWeight={800}>Fee Accounts</Typography>
              <Box sx={{ flex: 1 }} />
              {picked && <Chip size="small" label={`${accounts.length} account(s)`} sx={{ fontWeight: 700 }} />}
            </Stack>
            {!picked && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
                Pick a student to see their accounts.
              </Typography>
            )}
            {picked && accLoading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
            {picked && !accLoading && accounts.length === 0 && (
              <Alert severity="info" variant="outlined">No fee accounts on record.</Alert>
            )}
            {picked && !accLoading && accounts.map(a => {
              const sel = form.acc_name === a.account;
              return (
                <Paper key={a.account} variant="outlined" onClick={() => set('acc_name', a.account)}
                  sx={{
                    p: 1.2, mb: 1, cursor: 'pointer',
                    borderColor: sel ? 'primary.main' : 'divider',
                    borderWidth: sel ? 2 : 1,
                    bgcolor: sel ? (t) => alpha(t.palette.primary.main, 0.05) : 'background.paper',
                  }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={800} sx={{ ...TNUM }}>
                        {a.account}
                      </Typography>
                      <Stack direction="row" spacing={1.5} sx={{ mt: 0.4 }}>
                        <Typography variant="caption" color="text.secondary" sx={TNUM}>
                          Due: GHS {fmtMoney(a.total_due)}
                        </Typography>
                        <Typography variant="caption" color="success.dark" sx={TNUM}>
                          Paid: GHS {fmtMoney(a.t_paid)}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{
                        textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 800, fontSize: 9,
                        color: 'text.secondary', display: 'block', lineHeight: 1,
                      }}>Balance</Typography>
                      <Typography sx={{
                        fontWeight: 900, fontSize: 16, ...TNUM,
                        color: Number(a.balance) > 0 ? 'error.main' : 'text.disabled',
                      }}>
                        GHS {fmtMoney(a.balance)}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </CardContent>
        </Card>

        {/* ── Adjustment Form ─────────────────────────── */}
        <Card sx={{ alignSelf: 'flex-start', position: { lg: 'sticky' }, top: { lg: 100 } }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <AdjustIcon color="primary" />
              <Typography fontWeight={800}>New Adjustment</Typography>
            </Stack>

            <Stack spacing={1.2}>
              {/* Type */}
              <ToggleButtonGroup size="small" exclusive fullWidth value={form.type}
                onChange={(_, v) => v && set('type', v)}>
                <ToggleButton value="writeoff" sx={{ textTransform: 'none', fontWeight: 700 }}>
                  <WriteoffIcon fontSize="small" sx={{ mr: 0.6 }} /> Write-off
                </ToggleButton>
                <ToggleButton value="addbill" sx={{ textTransform: 'none', fontWeight: 700 }}>
                  <AddBillIcon fontSize="small" sx={{ mr: 0.6 }} /> Add Bill
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Account display (read-only mirror of picked card) */}
              <TextField size="small" fullWidth label="Account"
                value={form.acc_name}
                placeholder="Click an account on the left"
                InputProps={{ readOnly: true }} />

              {/* Amount with overflow check */}
              <TextField size="small" fullWidth type="number" label="Amount (GHS)"
                value={form.amount} onChange={(e) => set('amount', e.target.value)}
                error={overWriteoff}
                helperText={overWriteoff
                  ? `Exceeds current balance (${fmtMoney(selectedAccount?.balance)})`
                  : (selectedAccount
                    ? `Current balance: GHS ${fmtMoney(selectedAccount.balance)}`
                    : ' ')}
                inputProps={{ style: { textAlign: 'right', ...TNUM } }} />

              {/* New balance preview */}
              {newBalancePreview != null && (
                <Box sx={{
                  p: 1.2, borderRadius: 1.5,
                  bgcolor: (t) => alpha(
                    form.type === 'writeoff' ? t.palette.warning.main : t.palette.info.main, 0.08
                  ),
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 800, fontSize: 9, display: 'block' }}>
                        New Balance Preview
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                        Old: GHS {fmtMoney(selectedAccount.balance)}
                      </Typography>
                    </Box>
                    <Typography fontWeight={900} sx={{ ...TNUM, fontSize: 18,
                      color: form.type === 'writeoff' ? 'success.dark' : 'warning.dark' }}>
                      GHS {fmtMoney(newBalancePreview)}
                    </Typography>
                  </Stack>
                </Box>
              )}

              <TextField size="small" fullWidth label="Reason" multiline rows={2}
                value={form.reason} onChange={(e) => set('reason', e.target.value)}
                placeholder="At least 5 characters"
                error={form.reason.length > 0 && form.reason.trim().length < 5} />

              <TextField size="small" fullWidth label="Reference / Document (optional)"
                value={form.reference_doc} onChange={(e) => set('reference_doc', e.target.value)}
                placeholder="e.g. memo no, board minute" />

              <FormControlLabel
                sx={{ m: 0 }}
                control={<Switch size="small" checked={form.requires_approval}
                  onChange={(e) => set('requires_approval', e.target.checked)} />}
                label={
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Requires approval
                    {!canApprove && <Box component="span" sx={{ ml: 0.5, color: 'text.disabled' }}>
                      · forced (you don't have approval rights)
                    </Box>}
                  </Typography>
                }
                disabled={!canApprove}
              />

              <Divider />

              <Button fullWidth size="large" variant="contained"
                color={form.type === 'writeoff' ? 'warning' : 'primary'}
                onClick={submit} disabled={!canSubmit}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                sx={{ fontWeight: 800 }}>
                {submitting ? 'Saving…'
                  : (form.requires_approval || !canApprove)
                    ? 'Submit for Approval'
                    : 'Apply Adjustment'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {picked && (
        <Box sx={{ mt: 2 }}>
          <AdjustmentHistory items={history} loading={historyLoading} />
        </Box>
      )}
    </Box>
  );
}
